import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import Button from "@/components/common/button/Button";
import Input from "@/components/common/input/Input";
import Spinner from "@/components/common/spinner/Spinner";
import Modal from "@/components/common/modal/Modal";
import ManagementHeader from "@/features/admin/managementHeader/ManagementHeader";
import { getPageForRole } from "@/routes/routeMap";
import {
    deleteMyAccount,
    exportMyData,
    getMyData,
    updateMyData,
    type myData,
} from "@/api/complianceService";
import styles from "./MyData.module.css";

/** Página "Mis datos": ejercicio de los derechos del titular (Ley 1581). */
const MyData: React.FC = () => {
    const { logout, user } = useAuth();
    const navigate = useNavigate();

    // Cabecera con logo + "Volver" al inicio del rol + "Cerrar sesión".
    // No mostramos el botón "Mis datos" aquí (ya estamos en esa página).
    const homePath = getPageForRole(user?.role ?? null);
    const header = <ManagementHeader linkTo={homePath} showProfile={false} />;

    const [data, setData] = useState<myData | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        document.body.classList.add("solid-public-bg");
        return () => document.body.classList.remove("solid-public-bg");
    }, []);

    useEffect(() => {
        getMyData()
            .then(setData)
            .finally(() => setLoading(false));
    }, []);

    if (loading)
        return (
            <>
                {header}
                <Spinner text="Cargando tus datos..." />
            </>
        );
    if (!data)
        return (
            <>
                {header}
                <p className={styles["error"]}>No se pudieron cargar tus datos.</p>
            </>
        );

    const onField = (field: keyof myData, value: string) =>
        setData({ ...data, [field]: value });

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);
        try {
            const updated = await updateMyData({
                first_name: data.first_name,
                second_name: data.second_name,
                last_name: data.last_name,
                second_last_name: data.second_last_name,
                identifier: data.identifier,
            });
            setData(updated);
            setMessage("Datos actualizados correctamente.");
        } catch {
            setMessage("No se pudieron guardar los cambios.");
        } finally {
            setSaving(false);
        }
    };

    const handleDownload = async () => {
        const blob = await exportMyData();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `mis-datos-${data.id}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await deleteMyAccount();
            await logout();
            navigate("/login", { replace: true });
        } catch {
            setDeleting(false);
            setMessage("No se pudo eliminar la cuenta. Intenta de nuevo.");
            setConfirmOpen(false);
        }
    };

    return (
        <>
            {header}
            <main className={styles["mydata"]}>
            <h1 className={styles["title"]}>Mis datos personales</h1>
            <p className={styles["subtitle"]}>
                Aquí puedes consultar y corregir tus datos, descargar una copia, o
                eliminar tu cuenta y revocar el tratamiento de tus datos.
            </p>

            <section className={styles["card"]}>
                <h2>Datos de identidad</h2>
                <div className={styles["grid"]}>
                    <Input id="first_name" label="Primer nombre"
                        value={data.first_name || ""}
                        onChange={(e) => onField("first_name", e.target.value)} />
                    <Input id="second_name" label="Segundo nombre"
                        value={data.second_name || ""}
                        onChange={(e) => onField("second_name", e.target.value)} />
                    <Input id="last_name" label="Primer apellido"
                        value={data.last_name || ""}
                        onChange={(e) => onField("last_name", e.target.value)} />
                    <Input id="second_last_name" label="Segundo apellido"
                        value={data.second_last_name || ""}
                        onChange={(e) => onField("second_last_name", e.target.value)} />
                    <Input id="identifier" label="Documento"
                        value={data.identifier || ""}
                        onChange={(e) => onField("identifier", e.target.value)} />
                    <Input id="email" label="Correo (no editable)"
                        value={data.email} disabled />
                </div>
                <div className={styles["readonly-row"]}>
                    <span>Rol: <strong>{data.role}</strong></span>
                    <span>Institución: <strong>{data.institution || "—"}</strong></span>
                    <span>Fecha de nacimiento: <strong>{data.date_of_birth || "—"}</strong></span>
                </div>
                {message && <p className={styles["message"]}>{message}</p>}
                <Button
                    onClick={handleSave}
                    isLoading={saving}
                    loadingText="Guardando..."
                    className={styles["saveBtn"]}
                >
                    Guardar cambios
                </Button>
            </section>

            <section className={styles["card"]}>
                <h2>Mis participaciones</h2>
                {data.participations.length === 0 ? (
                    <p className={styles["muted"]}>Aún no tienes participaciones registradas.</p>
                ) : (
                    <ul className={styles["list"]}>
                        {data.participations.map((p, i) => (
                            <li key={i}>
                                <strong>{p.simulation}</strong>
                                {p.global_score != null && ` — Puntaje: ${p.global_score}`}
                                {p.global_percentile != null && ` (percentil ${p.global_percentile})`}
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section className={styles["card"]}>
                <h2>Mis derechos</h2>
                <div className={styles["actions"]}>
                    <Button
                        variant="secondary"
                        onClick={handleDownload}
                        className={styles["ghostBtn"]}
                    >
                        Descargar una copia de mis datos
                    </Button>
                    <Button
                        className={styles["danger"]}
                        onClick={() => setConfirmOpen(true)}
                    >
                        Eliminar mi cuenta y revocar el tratamiento
                    </Button>
                </div>
            </section>

            <Modal isOpen={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <div className={styles["confirm"]}>
                    <h2>¿Eliminar tu cuenta?</h2>
                    <p className={styles["warning"]}>
                        Esta acción es <strong>permanente e irreversible</strong>. Se
                        revocará el tratamiento de tus datos, tu información personal será
                        anonimizada y <strong>no podrás volver a ingresar</strong> ni
                        recuperar tus datos.
                    </p>
                    <p className={styles["muted"]}>
                        Te recomendamos descargar una copia de tus datos antes de continuar.
                    </p>
                    <div className={styles["confirm-actions"]}>
                        <Button
                            variant="secondary"
                            onClick={() => setConfirmOpen(false)}
                            className={styles["ghostBtn"]}
                        >
                            Cancelar
                        </Button>
                        <Button
                            className={styles["danger"]}
                            isLoading={deleting}
                            loadingText="Eliminando..."
                            onClick={handleDelete}
                        >
                            Sí, eliminar definitivamente
                        </Button>
                    </div>
                </div>
            </Modal>
            </main>
        </>
    );
};

export default MyData;
